import { Box, Tooltip, Typography, IconButton, TypographyOwnProps } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { FC } from 'react';
import Iconify from '../../media/iconify';

type CopyTextWithIconProps = {
  text: string;
  icon?: string;
  alwaysVisible?: boolean;
  iconPosition?: 'left' | 'right';
} & TypographyOwnProps;

export const CopyText: FC<CopyTextWithIconProps> = ({
  text,
  icon = 'solar:copy-bold-duotone',
  alwaysVisible = false,
  iconPosition = 'right',
  ...typographyProps
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();

  const handleCopy = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    e.preventDefault();
    try {
      await navigator.clipboard.writeText(text);
      enqueueSnackbar(t('Text copied to clipboard'), { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(t('Error copying text'), { variant: 'error' });
    }
  };

  const copyButton = (
    <Tooltip title={t('Copy to clipboard')} arrow placement="top">
      <IconButton
        size="small"
        onClick={handleCopy}
        className="copy-icon"
        sx={{
          p: '2px',
          m: 0,
          visibility: alwaysVisible ? 'visible' : 'hidden',
          opacity: alwaysVisible ? 1 : 0,
          transition: 'opacity 0.2s ease, visibility 0.2s ease',
          height: 22,
          width: 22,
        }}
      >
        <Iconify icon={icon} width={16} height={16} />
      </IconButton>
    </Tooltip>
  );

  const textElement = (
    <Typography
      variant="body1"
      sx={{
        py: 0,
        my: 0,
        lineHeight: 1.2,
        fontSize: '0.95em',
        ...(iconPosition === 'right' ? { marginRight: 0.5 } : { marginLeft: 0.5 }),
      }}
      {...typographyProps}
    >
      {text}
    </Typography>
  );

  return (
    <Box
      component="span"
      display="inline-flex"
      alignItems="center"
      sx={{
        position: 'relative',
        verticalAlign: 'middle',
        ...(alwaysVisible
          ? {}
          : {
              '&:hover .copy-icon': {
                visibility: 'visible',
                opacity: 1,
              },
            }),
      }}
    >
      {iconPosition === 'left' ? (
        <>
          {copyButton}
          {textElement}
        </>
      ) : (
        <>
          {textElement}
          {copyButton}
        </>
      )}
    </Box>
  );
};
